import { Env } from "@voghion/fe-util";

/**
 * 压缩队列，控制一次只执行一个压缩任务
 */
const queue = [];
let isRunning = false;

/**
 * 将文件读取为 array buffer
 * @param {*} file
 * @returns
 */
const readAsBuffer = (file) => {
  // 读取文件数据，传递给 worker
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (err) => {
      reject(err);
    };
    reader.readAsArrayBuffer(file);
  });
};

/**
 * 检查队列，执行下一个压缩任务
 */
const checkQueue = () => {
  const task = queue.shift();
  if (task) {
    innerCompressFile(task.file).then(task.resolve, task.reject);
  }
};

const env = Env.getInstance();

const innerCompressFile = (file) => {
  isRunning = true;
  return new Promise((resolve, reject) => {
    // 本来想充分利用 gpu 的，后面发现 pc 端再使用 webgl 处理图片，gpu 利用率已经很高了，没必要再多开 worker
    // let webCodecsWorkerNum = env.isEnvMobile ? 1 : Math.min(3, Math.ceil(file.size / 1024 / 1024 / 50)) // pc 端启用多个 worker 并发处理，充分利用资源
    // if (localStorage.getItem('debugWorkerNum')) {
    //   webCodecsWorkerNum = Math.min(3, Math.max(1, parseInt(localStorage.getItem('debugWorkerNum'))))
    // }
    let webCodecsWorkerNum = 1;
    let frameQueueLimit = 100;
    if (localStorage.getItem("frameQueueLimit")) {
      frameQueueLimit = Math.max(
        1,
        parseInt(localStorage.getItem("frameQueueLimit")),
      );
    }
    const startTime = Date.now();
    const mp4Worker = new Worker(
      new URL("@/utils/compress/worker-mp4.js", import.meta.url),
      { type: "module" },
    );
    const webCodecsWorkers = [];
    for (let i = 0; i < webCodecsWorkerNum; i++) {
      webCodecsWorkers.push(
        new Worker(
          new URL("@/utils/compress/worker-webcodecs.js", import.meta.url),
          { type: "module" },
        ),
      );
    }
    const safeClose = () => {
      isRunning = false;
      mp4Worker.postMessage({
        type: "close",
      });
      webCodecsWorkers.map((r) =>
        r.postMessage({
          type: "close",
        }),
      );
      setTimeout(checkQueue, 100);
    };
    try {
      const allWorkerNum = webCodecsWorkerNum + 1;
      let readyWorkerNum = 0;
      const CHUNK_SIZE = 512 * 1024 * 4; // 2M
      let chunkOffset = 0;
      const compressedChunkes = new Map();
      mp4Worker.onmessage = async (e) => {
        const { type, buffer, message, sendLast, data, samples } = e.data;
        switch (type) {
          case "init":
            {
              readyWorkerNum++;
              if (readyWorkerNum === allWorkerNum) {
                /**
                 * 在所有 worker 就绪后，开始发送第一个分片
                 */
                const newChunkOffset = chunkOffset + CHUNK_SIZE;
                const chunk = file.slice(chunkOffset, newChunkOffset);
                const buffer = await readAsBuffer(chunk);
                mp4Worker.postMessage({ type: "chunk", buffer, fileStart: 0 }, [
                  buffer,
                ]);
                chunkOffset = newChunkOffset;
                if (newChunkOffset > file.size) {
                  mp4Worker.postMessage({ type: "eof", fileSize: file.size });
                }
              }
            }
            break;
          case "nextChunk":
            {
              /**
               * 如果第一个分片没有 moov 信息，先发送最后一个分片的内容给 worker
               * 大部分 mp4 视频 moov 不在开头就在结尾
               */
              if (sendLast) {
                const chunkNum = Math.ceil(file.size / CHUNK_SIZE);
                let maxOffset = (chunkNum - 2) * CHUNK_SIZE;
                const chunk = file.slice(maxOffset);
                const buffer = await readAsBuffer(chunk);
                mp4Worker.postMessage(
                  { type: "chunk", buffer, fileStart: maxOffset },
                  [buffer],
                );
              } else {
                // 发送下一个分片
                const newChunkOffset = chunkOffset + CHUNK_SIZE;
                if (chunkOffset < file.size) {
                  const chunk = file.slice(chunkOffset, newChunkOffset);
                  const buffer = await readAsBuffer(chunk);
                  mp4Worker.postMessage(
                    { type: "chunk", buffer, fileStart: chunkOffset },
                    [buffer],
                  );
                }
                chunkOffset = newChunkOffset;
                if (newChunkOffset > file.size) {
                  mp4Worker.postMessage({ type: "eof", fileSize: file.size });
                }
              }
            }
            break;
          case "videoInfo":
            {
              webCodecsWorkers.map((r) =>
                r.postMessage({
                  type: "videoInfo",
                  data: data,
                }),
              );
            }
            break;
          case "samples":
            {
              const baseMessage = {
                type: "samples",
                isMobile: env.isEnvMobile,
                frameQueueLimit,
              };
              if (webCodecsWorkerNum === 1) {
                webCodecsWorkers[0].postMessage(
                  {
                    ...baseMessage,
                    samples,
                    index: 0,
                  },
                  samples.map((r) => r.data),
                );
              } else {
                /**
                 * 将 samples 分为多组，每组以关键帧开头
                 */
                const sampleLen = samples.length;
                const estimatedNum = Math.ceil(sampleLen / webCodecsWorkerNum);
                const chunks = [];
                let chunk = [];
                for (let i = 0; i < sampleLen; i++) {
                  const sample = samples[i];
                  if (
                    i > estimatedNum * (chunks.length + 1) &&
                    sample.type === "key"
                  ) {
                    chunks.push(chunk);
                    chunk = [];
                  }
                  chunk.push(sample);
                }
                chunks.push(chunk);
                webCodecsWorkers.forEach((webCodecsWorker, index) => {
                  const samples = chunks[index];
                  webCodecsWorker.postMessage(
                    {
                      ...baseMessage,
                      samples,
                      index: 0,
                    },
                    samples.map((r) => r.data),
                  );
                });
              }
            }
            break;
          case "success":
            {
              const blob = new Blob([buffer], { type: "video/mp4 " });
              safeClose();
              const costTime = Date.now() - startTime;
              console.log(
                "新视频文件大小：",
                (blob.size / 1024 / 1024).toFixed(3) + " MB",
              );
              resolve({
                costTime,
                blob,
              });
            }
            break;
          case "error":
            safeClose();
            reject(new Error(message));
            break;
        }
      };
      mp4Worker.onerror = (err) => {
        safeClose();
        reject(err);
      };
      webCodecsWorkers.forEach((webCodecsWorker) => {
        webCodecsWorker.onmessage = async (e) => {
          const { type, message, chunks, index } = e.data;
          switch (type) {
            case "init":
              {
                readyWorkerNum++;
                if (readyWorkerNum === allWorkerNum) {
                  /**
                   * 在所有 worker 就绪后，开始发送第一个分片
                   */
                  const newChunkOffset = chunkOffset + CHUNK_SIZE;
                  const chunk = file.slice(chunkOffset, newChunkOffset);
                  const buffer = await readAsBuffer(chunk);
                  mp4Worker.postMessage(
                    { type: "chunk", buffer, fileStart: 0 },
                    [buffer],
                  );
                  chunkOffset = newChunkOffset;
                  if (newChunkOffset > file.size) {
                    mp4Worker.postMessage({ type: "eof", fileSize: file.size });
                  }
                }
              }
              break;
            case "compressEnd":
              {
                compressedChunkes.set(index, chunks);
                if (compressedChunkes.size >= webCodecsWorkerNum) {
                  // 所有 worker 都压缩完成了，按顺序合并后传个 mp4 worker 合成
                  const allChunks = [];
                  for (let i = 0; i < webCodecsWorkerNum; i++) {
                    allChunks.push(...compressedChunkes.get(i));
                  }
                  mp4Worker.postMessage({
                    type: "muxVideo",
                    chunks: allChunks,
                  });
                }
              }
              break;
            case "error":
              safeClose();
              reject(new Error(message));
              break;
          }
        };
        webCodecsWorker.onerror = (err) => {
          safeClose();
          reject(err);
        };
      });
    } catch (err) {
      safeClose();
      reject(err);
    }
  });
};

/**
 * 压缩文件
 * @param {*} file
 * @returns
 */
export const compressFile = (file) => {
  return new Promise((resolve, reject) => {
    if (
      typeof window.VideoDecoder === "undefined" ||
      typeof window.VideoEncoder === "undefined"
    ) {
      /**
       * 如果不支持 webCodecs，直接报错，让外部去处理
       */
      return reject(new Error("The environment does not support comporess~"));
    }
    if (isRunning) {
      queue.push({
        file,
        resolve,
        reject,
      });
    } else {
      innerCompressFile(file).then(resolve, reject);
    }
  });
};
